import React, { createContext, useContext, useState, useEffect } from 'react';
import { Web3 } from 'web3';
import BFPETokenABI from '../contracts/BFPEToken.json';
import { getConfig } from '../utils/config';

const Web3Context = createContext();
const WALLET_MANUAL_DISCONNECT_KEY = 'bfpe.wallet.manualDisconnect';

export const useWeb3 = () => {
  const context = useContext(Web3Context);
  if (!context) {
    throw new Error('useWeb3 must be used within a Web3Provider');
  }
  return context;
};

export const Web3Provider = ({ children }) => {
  const [web3, setWeb3] = useState(null);
  const [account, setAccount] = useState(null);
  const [contract, setContract] = useState(null);
  const [chainId, setChainId] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState(null);
  const [owner, setOwner] = useState(null);
  const [blockNumber, setBlockNumber] = useState(0);
  const [refreshTick, setRefreshTick] = useState(0);
  const [manualDisconnected, setManualDisconnected] = useState(() => {
    try {
      return localStorage.getItem(WALLET_MANUAL_DISCONNECT_KEY) === '1';
    } catch {
      return false;
    }
  });

  const config = getConfig();
  const contractAddress = config.TOKEN_CONTRACT_ADDRESS;
  const rpcUrl = config.RPC_URL;
  const expectedChainId = config.CHAIN_ID;

  const getActiveAccount = (accounts, options = {}) => {
    const { preferPayload = false } = options;

    if (preferPayload && Array.isArray(accounts) && accounts.length > 0) {
      return accounts[0];
    }

    const selected = window.ethereum?.selectedAddress || null;
    if (selected) {
      if (!Array.isArray(accounts) || accounts.length === 0) return selected;
      const matched = accounts.find((a) => a.toLowerCase() === selected.toLowerCase());
      return matched || selected;
    }
    if (Array.isArray(accounts) && accounts.length > 0) return accounts[0];
    return null;
  };

  const syncWalletState = async () => {
    if (!window.ethereum) return;

    try {
      const [accounts, chainIdHex] = await Promise.all([
        window.ethereum.request({ method: 'eth_accounts' }),
        window.ethereum.request({ method: 'eth_chainId' }),
      ]);

      const activeAccount = getActiveAccount(accounts, { preferPayload: true });
      if (manualDisconnected) {
        setAccount(null);
      } else {
        setAccount(activeAccount);
      }

      const chainIdDec = parseInt(chainIdHex, 16).toString();
      setChainId(chainIdDec);

      if (!manualDisconnected && activeAccount && chainIdDec !== expectedChainId) {
        setError(`Por favor cambia a la red BFPE (Chain ID: ${expectedChainId})`);
      } else {
        setError(null);
      }
    } catch (err) {
      console.warn('No se pudo sincronizar el estado de MetaMask:', err);
    }
  };

  // Configuración para redes de desarrollo con minado rápido (Besu ethash).
  // Besu en modo dev puede minar decenas de bloques por segundo, por lo que
  // el timeout por defecto de web3.js v4 (50 bloques) expira antes de que
  // llegue el receipt. Se aplican valores más holgados tras crear la instancia.
  const applyWeb3Config = (web3Instance) => {
    // Some wallet providers report startBlock=0 for sent tx and web3 compares it
    // against current head immediately. Use a very high block timeout to avoid
    // false negatives while keeping polling timeout as the real wall-clock guard.
    web3Instance.eth.transactionBlockTimeout = 10000000; // bloques máx a esperar
    web3Instance.eth.transactionPollingInterval = 250; // poll cada 250 ms
    web3Instance.eth.transactionPollingTimeout = 300000; // timeout absoluto 300 s
    return web3Instance;
  };

  // Inicializar Web3 con el proveedor de Besu
  const initWeb3 = async () => {
    try {
      let web3Instance;

      // Intentar usar MetaMask u otro proveedor de wallet
      if (window.ethereum) {
        web3Instance = applyWeb3Config(new Web3(window.ethereum));
        console.log('✅ Usando proveedor de wallet del navegador');
      } else {
        // Fallback a HTTP Provider de Besu
        web3Instance = applyWeb3Config(new Web3(new Web3.providers.HttpProvider(rpcUrl)));
        console.log('✅ Usando HTTP Provider de Besu:', rpcUrl);
      }

      setWeb3(web3Instance);

      if (window.ethereum) {
        await syncWalletState();
      }

      // Inicializar contrato si existe la dirección
      if (contractAddress && contractAddress !== '') {
        const contractInstance = new web3Instance.eth.Contract(
          BFPETokenABI.abi,
          contractAddress
        );
        setContract(contractInstance);
        console.log('✅ Contrato inicializado:', contractAddress);

        // Obtener el owner del contrato
        try {
          const contractOwner = await contractInstance.methods.owner().call();
          setOwner(contractOwner.toLowerCase());
          console.log('✅ Owner del contrato:', contractOwner);
        } catch (ownerErr) {
          console.warn('No se pudo obtener el owner del contrato:', ownerErr);
        }
      }

      return web3Instance;
    } catch (err) {
      console.error('Error inicializando Web3:', err);
      setError('Error al inicializar Web3: ' + err.message);
      return null;
    }
  };

  // Conectar wallet
  const connectWallet = async () => {
    if (!window.ethereum) {
      setError('Por favor instala MetaMask o configura un proveedor Web3');
      return;
    }

    setIsConnecting(true);
    setError(null);

    try {
      setManualDisconnected(false);
      try {
        localStorage.setItem(WALLET_MANUAL_DISCONNECT_KEY, '0');
      } catch {}

      // Solicitar acceso a las cuentas
      const accounts = await window.ethereum.request({
        method: 'eth_requestAccounts',
      });

      if (accounts.length > 0) {
        const activeAccount = getActiveAccount(accounts, { preferPayload: true });
        setAccount(activeAccount);
        console.log('✅ Wallet conectada:', activeAccount);

        // Obtener chainId
        const chainIdHex = await window.ethereum.request({
          method: 'eth_chainId',
        });
        const chainIdDec = parseInt(chainIdHex, 16).toString();
        setChainId(chainIdDec);

        // Verificar si estamos en la red correcta
        if (chainIdDec !== expectedChainId) {
          console.warn(`⚠️ Red incorrecta. Esperada: ${expectedChainId}, Actual: ${chainIdDec}`);
          setError(`Por favor cambia a la red BFPE (Chain ID: ${expectedChainId})`);
        }
      }
    } catch (err) {
      console.error('Error conectando wallet:', err);
      setError('Error al conectar wallet: ' + err.message);
    } finally {
      setIsConnecting(false);
    }
  };

  // Cambiar o añadir la red Besu en MetaMask
  const switchToBesuNetwork = async () => {
    if (!window.ethereum) return;
    const chainIdHex = '0x' + parseInt(expectedChainId).toString(16);
    try {
      await window.ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: chainIdHex }],
      });
    } catch (switchError) {
      // 4902 = red no añadida aún
      if (switchError.code === 4902) {
        try {
          await window.ethereum.request({
            method: 'wallet_addEthereumChain',
            params: [{
              chainId: chainIdHex,
              chainName: config.NETWORK_NAME || 'BFPE Mainnet',
              rpcUrls: [config.RPC_URL || 'https://bfpe-mainnet.blockchain.tkn.eus'],
              nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
            }],
          });
        } catch (addError) {
          console.error('Error añadiendo la red:', addError);
          setError('No se pudo añadir la red BFPE a MetaMask');
        }
      } else {
        console.error('Error cambiando de red:', switchError);
      }
    }
  };

  // Forzar un refresco inmediato de balances y totalSupply
  const triggerRefresh = () => setRefreshTick(prev => prev + 1);

  // Desconectar wallet
  const disconnectWallet = () => {
    setManualDisconnected(true);
    try {
      localStorage.setItem(WALLET_MANUAL_DISCONNECT_KEY, '1');
    } catch {}
    setAccount(null);
    setError(null);
    console.log('🔌 Wallet desconectada');
  };

  // Escuchar cambios de cuenta
  useEffect(() => {
    if (window.ethereum) {
      const handleAccountsChanged = (accounts) => {
        if (accounts.length > 0) {
          if (manualDisconnected) {
            setAccount(null);
            console.log('🔄 Cuenta cambiada detectada, pero autoconexion desactivada por desconexion manual');
          } else {
            const activeAccount = getActiveAccount(accounts, { preferPayload: true });
            setAccount(activeAccount);
            console.log('🔄 Cuenta cambiada:', activeAccount);
          }
        } else {
          disconnectWallet();
        }
      };

      const handleChainChanged = (chainIdHex) => {
        const chainIdDec = parseInt(chainIdHex, 16).toString();
        setChainId(chainIdDec);
        console.log('🔄 Red cambiada:', chainIdDec);
        syncWalletState();
      };

      const handleWindowFocus = () => {
        syncWalletState();
      };

      const handleVisibilityChange = () => {
        if (document.visibilityState === 'visible') {
          syncWalletState();
        }
      };

      window.ethereum.on('accountsChanged', handleAccountsChanged);
      window.ethereum.on('chainChanged', handleChainChanged);
      window.addEventListener('focus', handleWindowFocus);
      document.addEventListener('visibilitychange', handleVisibilityChange);

      return () => {
        window.ethereum.removeListener('accountsChanged', handleAccountsChanged);
        window.ethereum.removeListener('chainChanged', handleChainChanged);
        window.removeEventListener('focus', handleWindowFocus);
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      };
    }
  }, [manualDisconnected]);

  // Inicializar Web3 al montar
  useEffect(() => {
    initWeb3();
  }, []);

  // Polling de número de bloque cada 10 s para detectar nuevos bloques
  useEffect(() => {
    if (!web3) return;
    let active = true;
    const poll = async () => {
      try {
        const n = Number(await web3.eth.getBlockNumber());
        if (active) {
          setBlockNumber(n);
          setRefreshTick(prev => prev + 1);
        }
      } catch (_) {}
    };
    poll();
    const id = setInterval(poll, 10000);
    return () => { active = false; clearInterval(id); };
  }, [web3]);

  const isOwner = !!(account && owner && account.toLowerCase() === owner);

  const value = {
    web3,
    account,
    contract,
    chainId,
    isConnecting,
    error,
    contractAddress,
    owner,
    isOwner,
    blockNumber,
    refreshTick,
    connectWallet,
    disconnectWallet,
    switchToBesuNetwork,
    triggerRefresh,
  };

  return <Web3Context.Provider value={value}>{children}</Web3Context.Provider>;
};
